<template>
  <div class="d-flex justify-content-end">
    <McTableFilter
      :activeFilters="activeFilters"
      :parseFilterDataMappings="parseFilterDataMappings"
      @submit="handleInput"
    >
      <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
        <div class="mc-modal__filter-item">
          <FormulateInput
            name="tripPeriod"
            isShow="true"
            :label="$t('landMeasurement.field.tripPeriod')"
            :placeholder="`${$t('global.form.select')} ${$t('global.field.date')}`"
            type="date-picker"
            datepickerType="datetime"
            valueType="YYYY-MM-DDTHH:mm:ss.SSSZ"
            format="DD MMM YYYY HH:mm"
            titleFormat="DD MMM YYYY"
            rangeSeparator=" - "
            :range="true"
            :draft="true"
            :confirm="true"
            :confirmText="$t('global.action.apply')"
            :value="formValues.tripPeriod"
            :shortcuts="makeShortcuts"
          >
          </FormulateInput>
        </div>
        <div class="mc-modal__filter-item">
          <FormulateInput
            name="licensePlate"
            :label="$t('global.object.licensePlate')"
            :placeholder="`${$t('global.form.select')} ${$t('global.object.licensePlate')}`"
            :multiple="true"
            type="value-help-v2"
            trackBy="id"
            :displayedBy="'licensePlate'"
            :listGetter="resourcefulQuery.vehicle"
            :tableFields="vehicleTableFields"
            :fullObject="fullObjects['licensePlate']"
            @update:formValues="updateFormValues({ key: 'licensePlate', value: $event })"
            @update:fullObject="updateFullObjects({ key: 'licensePlate', value: $event })"
            :value="formValues.licensePlate"
          >
          </FormulateInput>
        </div>
        <div class="mc-modal__filter-item">
          <FormulateInput
            name="fullname"
            :label="$t('global.object.createdBy')"
            :placeholder="`${$t('global.form.select')} ${$t('global.object.createdBy')}`"
            :multiple="true"
            type="value-help-v2"
            trackBy="userId"
            :displayedBy="'fullname'"
            :listGetter="resourcefulQuery.user"
            :tableFields="userTableFields"
            :fullObject="fullObjects['fullname']"
            @update:formValues="updateFormValues({ key: 'fullname', value: $event })"
            @update:fullObject="updateFullObjects({ key: 'fullname', value: $event })"
            :value="formValues.fullname"
          >
          </FormulateInput>
        </div>
      </template>
    </McTableFilter>

    <div class="ms-2">
      <McButton
        v-if="isSelected"
        @click="showDeleteModal"
        :variant="'secondary-red'"
        :text="$t('global.delete')"
        icon="icon-outline_trash"
      />
    </div>
  </div>
</template>

<script>
import McTableFilter from '@/components/McTableFilter'
import McButton from '@/components/McButton'
import { GET_VEHICLES_PAGINATED } from '@/services/api/master/fleet'
import { GET_USERS } from '@/services/api/master/user'

export default {
  components: {
    McTableFilter,
    McButton,
  },

  props: {
    activeFilters: {
      required: true,
    },
    handleChangeActiveFilters: {
      type: Function,
    },
    isSelected: {},
    showDeleteModal: {},
  },

  data() {
    return {}
  },

  computed: {
    vehicleTableFields() {
      return [
        {
          label: this.$t('global.object.licensePlate'),
          key: 'licensePlate',
          isSortable: false,
          isRedirectable: true,
        },
        {
          label: this.$t('global.field.vehicleType'),
          key: 'vehicleType',
          formatter(val) {
            const label = [val.category, val.model, val.type]
            return label.filter((x) => x !== null).join('/')
          },
          isSortable: false,
        },
      ]
    },
    userTableFields() {
      return [
        {
          label: this.$t('global.object.createdBy'),
          key: 'fullname',
          isSortable: false,
          isRedirectable: true,
        },
      ]
    },
    resourcefulQuery() {
      return {
        vehicle: async (opts) => {
          let result = {}

          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            orderBy: opts.sorts,
            search: opts.search,
          }

          const res = await this.$apollo.query({
            query: GET_VEHICLES_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: filter,
            },
          })

          const data = res.data.vehiclesPaginated.vehiclesPaginated

          result = {
            metadata: {
              total_count: data.pagination.total,
              page: data.pagination.page,
            },
            data: {
              paginated_result: data.data,
            },
          }

          return result
        },
        user: async (opts) => {
          const filter = {
            search: opts.search,
          }

          const res = await this.$apollo.query({
            query: GET_USERS,
            fetchPolicy: 'no-cache',
            variables: {
              filter: filter,
            },
          })

          const data = res.data.users.users

          const filteredAndSortedUsers = data
            .filter((user) => !user.position || user.position.name !== 'Driver')
            .sort((a, b) => a.fullname.localeCompare(b.fullname))

          const result = {
            metadata: {
              total_count: data.length,
              page: 1,
            },
            data: {
              paginated_result: filteredAndSortedUsers,
            },
            isPagination: false,
          }

          return result
        },
      }
    },
    parseFilterDataMappings() {
      return {
        tripPeriod: 'tripTime',
        licensePlate: 'vehicleId',
        fullname: 'userId',
      }
    },

    makeShortcuts() {
      return [
        {
          text: this.$t('global.time.today'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setHours(0, 0, 0, 0)
            endDate.setHours(23, 59, 59, 0)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.yesterday'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(startDate.getDate() - 1)
            startDate.setHours(0, 0, 0, 0)

            endDate.setDate(endDate.getDate() - 1)
            endDate.setHours(23, 59, 59, 0)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.last7Days'),
          onClick: () => {
            const endDate = new Date()
            const startDate = new Date()

            startDate.setDate(startDate.getDate() - 6)
            startDate.setHours(0, 0, 0, 0)

            endDate.setHours(23, 59, 59, 0)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.last30Days'),
          onClick: () => {
            const endDate = new Date()
            const startDate = new Date()

            startDate.setDate(startDate.getDate() - 29)
            startDate.setHours(0, 0, 0, 0)

            endDate.setHours(23, 59, 59, 0)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.thisMonth'),
          onClick: () => {
            const endDate = new Date()
            const startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1)

            startDate.setHours(0, 0, 0, 0)
            endDate.setHours(23, 59, 59, 0)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.lastMonth'),
          onClick: () => {
            const startDate = new Date()
            startDate.setTime(startDate.getTime() - 3600 * 1000 * 24 * startDate.getDate())
            startDate.setTime(startDate.getTime() - 3600 * 1000 * 24 * (startDate.getDate() - 1))

            const endDate = new Date()
            endDate.setTime(endDate.getTime() - 3600 * 1000 * 24 * endDate.getDate())

            startDate.setHours(0, 0, 0, 0)
            endDate.setHours(23, 59, 59, 0)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.allTime'),
          onClick: () => {
            return [null, null]
          },
        },
      ].map((shortcut) => ({ ...shortcut, value: shortcut.onClick }))
    },
  },

  methods: {
    handleInput(value) {
      this.$emit('handleChangeActiveFilters', value)
    },
  },
}
</script>
